import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows, fetchAllRowsResult } from '../../lib/fetchAll';
import { bucketOf } from '../../lib/tagBuckets';
import { COUNT_TIPS } from '../lib/adminCounts';
import InfoTip from '../components/InfoTip';
import { logAudit } from '../lib/auditLog';
import BuyLinksEditor from '../components/BuyLinksEditor';
import { LINK_WORDS } from '../lib/buyLinks';
import { okToLeave } from '../../lib/unsavedChanges';
import { useTabParam } from '../../hooks/useTabParam';
import { requestBookReembed } from '../../lib/reembedBook';
import { BookPills, DescriptionExpander, FixAuthorName } from '../components/BookBits';
import GenresPanel from '../components/GenresPanel';
import { loadCounts, SETS } from '../lib/healthCounts';
import { BOOK_ROW_FIELDS, testDataReasons, isSwipeable, sortTestData } from '../lib/bookRowInfo';
import { POPULAR_EDITIONS, POPULAR_RATINGS, POPULAR_LINE_TEXT, POPULARITY_LABEL, classifyPopularity, readingsText } from '../../lib/popularity';

// A status change that must land: an error or an empty result is a failure, and the audit line
// is written only after the row is confirmed changed.
async function setBookStatus(book, to, auditText) {
  const { data, error } = await supabase.from('books').update({ status: to }).eq('id', book.id).select('id');
  if (error) return { error: error.message };
  if (!data?.length) return { error: 'the database changed nothing' };
  await logAudit('update', 'book', auditText, book.id);
  return { error: null };
}

// The book a link to one book (?book=id) points at goes first in its list, so its row is the first
// one on the first page. Everything else keeps its order.
// While the buy-links editor has changes that are not saved, its book's row stays on screen even when
// a search, a filter or the page would drop it, so the changes are not lost without a word.
function keepOpenRow(slice, all, id, unsaved) {
  if (!unsaved || id == null || slice.some(b => b.id === id)) return slice;
  const row = all.find(b => b.id === id);
  return row ? [row, ...slice] : slice;
}

function focusFirst(list, id) {
  if (!id) return list;
  const hit = list.find(b => b.id === id);
  return hit ? [hit, ...list.filter(b => b !== hit)] : list;
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Pill({ children, color = 'gray' }) {
  const styles = {
    gray:   { background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    green:  { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
    amber:  { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
    blue:   { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
    purple: { background: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe' },
    red:    { background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1px solid #fca5a5' },
    dark:   { background: 'var(--neutral-800)', color: 'var(--feedback-warning-bg)', border: '1px solid var(--neutral-800)' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 'var(--font-size-xxs)',
      fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-xxl)',
      letterSpacing: 0.2, whiteSpace: 'nowrap', flexShrink: 0,
      ...styles[color],
    }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = 'default', disabled, small }) {
  const variants = {
    default: { background: 'var(--brand-white)',    color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    approve: { background: 'var(--feedback-success-bg)', color: '#065f46', border: '1px solid #86efac' },
    reject:  { background: '#fff8f8', color: '#991b1b', border: '1px solid #fca5a5' },
    primary: { background: 'var(--neutral-800)', color: 'var(--brand-white)',    border: '1px solid var(--neutral-800)' },
    amber:   { background: '#fffbeb', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontSize: small ? 11 : 11.5, fontWeight: 600,
      padding: small ? '4px 10px' : '6px 13px',
      borderRadius: 'var(--radius-md)', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
      opacity: disabled ? 0.5 : 1, ...variants[variant],
    }}>
      {children}
    </button>
  );
}

function PaginationBar({ cur, pages, total, per, onPrev, onNext }) {
  if (total <= per) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'var(--space-14)', marginTop: 'var(--space-6)', borderTop: '1px solid var(--brand-cream)' }}>
      <span style={{ fontSize: 11.5, color: 'var(--neutral-400)' }}>
        Showing {cur * per + 1}–{Math.min(cur * per + per, total)} of {total}
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
        <button onClick={onPrev} disabled={cur === 0} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur === 0 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 11.5, fontWeight: 600, cursor: cur === 0 ? 'default' : 'pointer', fontFamily: 'var(--font-body)' }}>← Prev</button>
        <span style={{ fontSize: 11.5, color: '#8b7355' }}>{cur + 1}/{pages}</span>
        <button onClick={onNext} disabled={cur >= pages - 1} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur >= pages - 1 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 11.5, fontWeight: 600, cursor: cur >= pages - 1 ? 'default' : 'pointer', fontFamily: 'var(--font-body)' }}>Next →</button>
      </div>
    </div>
  );
}

const SOURCE_STYLES = {
  author: { bg: '#fffbeb', color: '#78350f', border: 'var(--feedback-warning-border)', dot: '#d97706', tip: 'Added by the author via Author Portal. Highest confidence.' },
  reader: { bg: '#f0fdf4', color: '#065f46', border: '#a7f3d0', dot: '#16a34a', tip: 'Added or confirmed by the reader who submitted this book.' },
  api:    { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe', dot: '#2563eb', tip: 'Pulled from Google Books API. Lowest confidence, unverified.' },
};

function TagSourceChip({ source, count }) {
  const s = SOURCE_STYLES[source] || SOURCE_STYLES.api;
  const labels = { author: `${count} author`, reader: `${count} reader`, api: `${count} api` };
  return (
    <span title={s.tip} style={{
      display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)',
      fontSize: 10.5, fontWeight: 600, padding: '3px 9px',
      borderRadius: 'var(--radius-lg)', cursor: 'help',
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 'var(--radius-circle)', background: s.dot, flexShrink: 0 }} />
      {labels[source] || `${count} ${source}`}
    </span>
  );
}

function BookCover({ url, size = 'sm' }) {
  const w = size === 'lg' ? 48 : 32;
  const h = size === 'lg' ? 68 : 46;
  return (
    <div style={{
      width: w, height: h, borderRadius: '3px 7px 7px 3px', flexShrink: 0,
      background: 'var(--brand-cream)', border: '1px solid var(--brand-sand)',
      overflow: 'hidden', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 'var(--font-size-lg)',
      boxShadow: '2px 2px 5px rgba(0,0,0,0.07)',
    }}>
      {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📚'}
    </div>
  );
}

function TagEditor({ bookId, onClose }) {
  const [tags, setTags]           = useState([]);
  const [allTropes, setAllTropes] = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    async function load() {
      // The trope list is the picker's whole vocabulary, so capping it at
      // 1,000 would silently hide tropes from the dropdown rather than fail.
      const [tagRows, tropeRows] = await Promise.all([
        fetchAllRows(() => supabase.from('book_tags').select('id, tag_type, source, trope:tropes(id, canonical_name, category)').eq('book_id', bookId)),
        fetchAllRows(() => supabase.from('tropes').select('id, canonical_name, category').eq('is_active', true)),
      ]);
      tropeRows.sort((a, b) => (a.canonical_name || '').localeCompare(b.canonical_name || ''));
      setTags(tagRows || []);
      setAllTropes(tropeRows || []);
      setLoading(false);
    }
    load();
  }, [bookId]);

  async function removeTag(tagId) {
    // Was: delete, then drop it from the list regardless. A refused delete left
    // the tag on the book and off the screen — the exact shape of an edit that
    // "saved" and did not. Check first, then update the list.
    const { error } = await supabase.from('book_tags').delete().eq('id', tagId);
    if (error) { alert(`Tag NOT removed, it is still on the book: ${error.message}`); return; }
    setTags(prev => prev.filter(t => t.id !== tagId));
    await logAudit('delete', 'book', `Removed tag from book ${bookId}`, bookId);
  }

  async function addTag(trope) {
    if (tags.some(t => t.trope?.id === trope.id)) return;
    setSaving(true);

    // TAG_TYPE IS NOT CATEGORY, and this line used to write one into the other:
    //
    //     tag_type: trope.category || 'trope'
    //
    // book_tags_tag_type_check permits exactly eight lowercase strings
    // (live_schema_snapshot.sql:1752). tropes.category has no CHECK at all and
    // still carries 'Representation' with a capital R — see CATEGORY_TO_BUCKET
    // in src/lib/tagBuckets.js, which maps it to 'representation'. Replayed
    // against the real constraint: adding any Representation tag from this
    // screen was REJECTED outright, and the admin got a raw Postgres error.
    //
    // Every other tag worked by luck, because most categories happen to be
    // spelled the same as their bucket. 20260905040000 folded the five Title
    // Case sub-groups, but process-trope writes whatever category it is handed,
    // so a new unmapped one can appear at any time. bucketOf is the one place
    // that knows the mapping, and it returns null rather than guessing.
    const bucket = bucketOf(trope.category);
    if (!bucket) {
      // An unmapped category is an admin data problem, not something to paper
      // over with a silent default. Say which value, so it can be found.
      alert(
        `Tag NOT added. "${trope.canonical_name}" is filed under the category ` +
        `${JSON.stringify(trope.category)}, which is not one of the eight tag buckets. ` +
        `Fix the category in the trope catalogue, or add it to CATEGORY_TO_BUCKET in src/lib/tagBuckets.js.`
      );
      setSaving(false);
      return;
    }

    // 'author', not 'reader'. Founder's ruling: an admin adding a tag is acting
    // for the book, so it is recorded the way an author's tag is rather than
    // inventing a fourth source value. book_tags_source_check allows only
    // author / api / reader, so 'reader' was not merely wrong, it was the one
    // value that makes the tag look like a stranger's guess — and since an
    // author's edit now preserves reader tags rather than clearing them
    // (author repo, fix/author-edit-keeps-reader-tags), a mislabelled row would
    // outlive every edit claiming to be somebody else's.
    //
    const { data, error } = await supabase
      .from('book_tags')
      .insert({ book_id: bookId, trope_id: trope.id, tag_type: bucket, source: 'author' })
      .select('id, tag_type, source, trope:tropes(id, canonical_name, category)')
      .single();
    // `if (!error && data)` did destructure the error and then said nothing
    // about it: the tag simply never appeared, with no explanation. Silence is
    // not error handling.
    if (error) { alert(`Tag NOT added: ${error.message}`); setSaving(false); return; }
    if (!data)  { alert('Tag NOT added, the insert returned no row.'); setSaving(false); return; }
    setTags(prev => [...prev, data]);
    await logAudit('create', 'book', `Added tag "${trope.canonical_name}" to book ${bookId}`, bookId);
    setSaving(false);
  }

  const filtered = allTropes.filter(t =>
    !tags.some(tag => tag.trope?.id === t.id) &&
    bucketOf(t.category) !== 'genre' &&
    t.canonical_name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={{ padding: '12px 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Loading tags...</div>;

  return (
    <div style={{ marginTop: 'var(--space-md)', padding: '14px 16px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-10)' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)' }}>Edit tags</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-md)', cursor: 'pointer', color: 'var(--neutral-400)' }}>✕</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 'var(--space-10)' }}>
        {tags.length === 0 && <span style={{ fontSize: 11.5, color: 'var(--neutral-400)', fontStyle: 'italic' }}>No tags yet</span>}
        {tags.map(tag => (
          <span key={tag.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 500, padding: '4px 10px', borderRadius: 14, background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' }}>
            {tag.trope?.canonical_name || tag.tag_type}
            <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--neutral-400)' }}>({tag.source})</span>
            <span onClick={() => removeTag(tag.id)} style={{ cursor: 'pointer', color: 'var(--neutral-400)', fontWeight: 700, fontSize: 'var(--font-size-sm)', lineHeight: 1 }}
              onMouseEnter={e => e.target.style.color = '#dc2626'}
              onMouseLeave={e => e.target.style.color = '#a8a29e'}>×</span>
          </span>
        ))}
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tropes to add..."
        style={{ width: '100%', padding: '7px 10px', fontSize: 'var(--font-size-sm)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', marginBottom: 'var(--space-sm)' }}
      />
      <div style={{ maxHeight: 140, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {filtered.slice(0, 30).map(trope => (
          <span key={trope.id} onClick={() => !saving && addTag(trope)}
            style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 'var(--radius-lg)', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', cursor: saving ? 'not-allowed' : 'pointer' }}
            onMouseEnter={e => { e.target.style.background = '#fef3c7'; e.target.style.borderColor = '#fde68a'; }}
            onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.borderColor = '#e8e4dc'; }}>
            + {trope.canonical_name}
          </span>
        ))}
        {filtered.length === 0 && search && <span style={{ fontSize: 11.5, color: 'var(--neutral-400)', fontStyle: 'italic' }}>No matching tropes</span>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PENDING QUEUE — every pending book, whatever its tag count (ticket 60)
// ══════════════════════════════════════════════════════════════════════════════
function PendingQueue({ onCountChange, onBadgeChange, isMobile, emb, onFlagged, onNotice, focusBookId }) {
  const [books, setBooks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [working, setWorking]           = useState(null);
  const [editingTags, setEditingTags]   = useState(null);
  const [editingLinks, setEditingLinks] = useState(null);
  const [linksUnsaved, setLinksUnsaved] = useState(false);   // the open buy-links editor has changes that are not saved
  // Closing, or opening another book's, drops what was not saved, so ask first.
  // Next and Previous: a yes closes the editor (the book's row would leave this page), so a yes does what
  // the question says and the change is gone only because the person said so.
  function turnPage(update) {
    if (!okToLeave()) return;
    setEditingLinks(null);
    setPage(update);
  }
  function toggleLinks(id) {
    if (editingLinks !== null && linksUnsaved && !window.confirm(LINK_WORDS.closeAnyway)) return;
    setEditingLinks(editingLinks === id ? null : id);
  }
  const [editingGenres, setEditingGenres] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage]                 = useState(0);
  const [loadError, setLoadError]       = useState(null);
  const PER = 10;

  const onCountRef = useRef(onCountChange);
  const onBadgeRef = useRef(onBadgeChange);
  useEffect(() => { onCountRef.current = onCountChange; }, [onCountChange]);
  useEffect(() => { onBadgeRef.current = onBadgeChange; }, [onBadgeChange]);
  useEffect(() => { setPage(0); }, [sourceFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    // Every pending book. Supabase caps a response at 1,000 rows even without
    // a .limit(), and the pagination total below is this array's length, so a
    // cap would both misreport the count and hide the extra books.
    const res = await fetchAllRowsResult(() => supabase
      .from('books')
      .select(`id, title, author_name, cover_image_url, status, submission_type, entry_point, flag_reason, created_at, live_at, genres, claimed_at, author_id, description_source, reader_description, is_author_described, edition_count, ratings_count, popularity_checked_at, popularity_override:book_popularity_overrides ( class, reason, set_at ), claimant:authors!books_author_id_fkey ( id, display_name, email ), book_tags ( id, tag_type, source, trope:tropes(id, canonical_name) )`)
      .eq('status', 'pending'));
    if (!res.ok) {
      console.error('[Books] pending list failed:', res.error);
      setBooks([]); setLoadError(res.error); setLoading(false);
      onCountRef.current?.(null);
      return;
    }
    setLoadError(null);
    const data = res.rows;

    let submitterMap = {};
    try {
      const { data: subs } = await supabase.from('book_submissions').select('submitted_by, title').limit(200);
      if (subs?.length) {
        const userIds = [...new Set(subs.map(s => s.submitted_by).filter(Boolean))];
        if (userIds.length) {
          const { data: profiles } = await supabase.from('user_profiles').select('user_id, username').in('user_id', userIds);
          const profileMap = {};
          (profiles || []).forEach(p => { profileMap[p.user_id] = p.username; });
          subs.forEach(s => {
            if (s.submitted_by && profileMap[s.submitted_by])
              submitterMap[s.title?.toLowerCase().trim()] = profileMap[s.submitted_by];
          });
        }
      }
    } catch (err) { console.warn('Submitter lookup failed:', err.message); }

    const enriched = (data || [])
      // fetchAllRows pages by id, so the newest-first order this queue was
      // shown in is reapplied here.
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .map(b => ({
        ...b,
        submitter: submitterMap[b.title?.toLowerCase().trim()] || null,
        tag_source_counts: {
          author: (b.book_tags || []).filter(t => t.source === 'author').length,
          reader: (b.book_tags || []).filter(t => t.source === 'reader').length,
          api:    (b.book_tags || []).filter(t => t.source === 'api').length,
        },
      }));

    setBooks(enriched);
    onCountRef.current?.(enriched.length);
    onBadgeRef.current?.('books', enriched.length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // A link to one book (?book=id) opens its buy links, once, when its row is in this list.
  const openedFor = useRef(null);
  useEffect(() => {
    if (focusBookId && openedFor.current !== focusBookId && books.some(b => b.id === focusBookId)) {
      openedFor.current = focusBookId;
      setEditingLinks(focusBookId);
      setPage(0);
    }
  }, [focusBookId, books]);

  async function handleFlag(book) {
    if (!confirm(`Flag "${book.title}"? It moves to the Flagged tab and stays out of the catalog until you un-flag it. Nothing is deleted.`)) return;
    setWorking(book.id);
    const { error } = await setBookStatus(book, 'flagged', `Flagged "${book.title}" (was ${book.status})`);
    if (error) { alert(`"${book.title}" NOT flagged, it is still ${book.status}: ${error}`); setWorking(null); return; }
    onFlagged?.();
    await load(); setWorking(null);
  }

  async function handlePublish(book) {
    setWorking(book.id);
    const { error } = await supabase.from('books').update({ status: 'live' }).eq('id', book.id);
    if (error) { alert('Error: ' + error.message); setWorking(null); return; }
    await logAudit('create', 'book', `Published "${book.title}" by ${book.author_name}`, book.id);
    load(); setWorking(null);
  }

  async function handleReject(book) {
    const reason = prompt('Rejection reason (optional):') ?? '';
    setWorking(book.id);
    const { error } = await supabase.from('books').update({ status: 'removed', removal_reason: reason || null }).eq('id', book.id);
    if (error) { alert('Error: ' + error.message); setWorking(null); return; }
    await logAudit('delete', 'book', `Rejected "${book.title}", ${reason || 'no reason given'}`, book.id);
    load(); setWorking(null);
  }

  const SOURCE_FILTER_OPTIONS = [
    { id: 'all', label: 'All' }, { id: 'reader', label: 'Reader' },
    { id: 'csv', label: 'CSV' }, { id: 'author', label: 'Author' }, { id: 'admin', label: 'Admin' },
  ];

  const SUBMISSION_TYPE_PILL = {
    reader: { color: 'purple', label: 'Reader' },
    csv:    { color: 'green',  label: 'CSV' },
    author: { color: 'dark',   label: 'Author' },
    admin:  { color: 'gray',   label: 'Admin' },
  };

  const filtered = focusFirst(sourceFilter === 'all' ? books : books.filter(b => b.submission_type === sourceFilter), focusBookId);
  const pages    = Math.max(1, Math.ceil(filtered.length / PER));
  const curPage  = Math.min(page, pages - 1);
  const slice    = filtered.slice(curPage * PER, curPage * PER + PER);
  const shown    = keepOpenRow(slice, books, editingLinks, linksUnsaved);

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading queue...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap', padding: '8px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-lg)' }}>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Source:</span>
        {SOURCE_FILTER_OPTIONS.map(f => {
          const count = f.id === 'all' ? books.length : books.filter(b => b.submission_type === f.id).length;
          return (
            <button key={f.id} onClick={() => setSourceFilter(f.id)} style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontFamily: 'var(--font-body)',
              background: sourceFilter === f.id ? 'var(--neutral-800)' : 'var(--brand-white)',
              color:      sourceFilter === f.id ? 'var(--brand-white)'    : 'var(--neutral-500)',
              border:     sourceFilter === f.id ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
            }}>
              {f.label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {loadError && <LoadFailed what="the pending books" message={loadError} />}

      {!loadError && !slice.length && (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
          ✓ {sourceFilter === 'all' ? 'No books pending review' : `No ${sourceFilter} submissions pending`}
        </div>
      )}

      {shown.map((book, idx) => {
        const busy      = working === book.id;
        const last      = idx === shown.length - 1;
        const typeInfo  = SUBMISSION_TYPE_PILL[book.submission_type] ?? { color: 'gray', label: book.submission_type || 'Submission' };
        const hasTags   = Object.values(book.tag_source_counts).some(c => c > 0);
        const totalTags = Object.values(book.tag_source_counts).reduce((a, b) => a + b, 0);

        return (
          <div key={book.id}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)', padding: '16px 0', borderBottom: last && editingTags !== book.id && editingGenres !== book.id ? 'none' : '1px solid var(--brand-cream)' }}>
              <BookCover url={book.cover_image_url} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>{book.title}</div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginBottom: 'var(--space-6)' }}>
                      {book.author_name}
                      {book.submitter && <> · <strong style={{ color: 'var(--neutral-600)' }}>@{book.submitter}</strong></>}
                      <> · <span style={{ color: 'var(--neutral-400)' }}>{timeAgo(book.created_at)}</span></>
                    </div>
                  </div>
                  <Pill color={typeInfo.color}>{typeInfo.label}</Pill>
                </div>
                <ClassificationColumns book={book} />
                <BookPills book={book} embedding={emb} />
                <DescriptionExpander bookId={book.id} />
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 'var(--space-6)' }}>
                  {!hasTags
                    ? <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No tags yet</span>
                    : <>
                        {book.tag_source_counts.author > 0 && <TagSourceChip source="author" count={book.tag_source_counts.author} />}
                        {book.tag_source_counts.reader > 0 && <TagSourceChip source="reader" count={book.tag_source_counts.reader} />}
                        {book.tag_source_counts.api    > 0 && <TagSourceChip source="api"    count={book.tag_source_counts.api} />}
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', alignSelf: 'center' }}>{totalTags} total</span>
                      </>
                  }
                </div>
                {book.genres?.length > 0 && (
                  <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
                    {book.genres.slice(0, 4).map(g => (
                      <span key={g} style={{ fontSize: 'var(--font-size-xxs)', padding: '2px 7px', borderRadius: 'var(--radius-md)', background: 'var(--brand-cream)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)' }}>{g}</span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                  <Btn variant="approve" onClick={() => handlePublish(book)} disabled={busy}>Publish</Btn>
                  <Btn variant="amber" onClick={() => setEditingTags(editingTags === book.id ? null : book.id)} disabled={busy}>
                    {editingTags === book.id ? 'Close tags' : 'Edit tags'}
                  </Btn>
                  <Btn onClick={() => toggleLinks(book.id)} disabled={busy}>
                    {editingLinks === book.id ? 'Close buy links' : 'Edit buy links'}
                  </Btn>
                  <Btn onClick={() => setEditingGenres(editingGenres === book.id ? null : book.id)} disabled={busy}>
                    {editingGenres === book.id ? 'Close genres' : 'Genres'}
                  </Btn>
                  <Btn variant="amber" onClick={() => handleFlag(book)} disabled={busy}>Flag</Btn>
                  <FixAuthorName book={book} onFixed={load} />
                  <Btn variant="reject" onClick={() => handleReject(book)} disabled={busy}>Reject</Btn>
                </div>
              </div>
            </div>
            {editingGenres === book.id && (
              <div style={{ paddingBottom: 14 }}>
                <GenresPanel book={book} onClose={() => setEditingGenres(null)} onChanged={(text) => { onNotice?.(text); load(); onFlagged?.(); }} />
              </div>
            )}
            {editingTags === book.id && (
              <div style={{ paddingBottom: last ? 0 : 16, borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
                <TagEditor bookId={book.id} onClose={() => setEditingTags(null)} />
              </div>
            )}
            {editingLinks === book.id && (
              <div style={{ paddingBottom: last ? 0 : 16, borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
                <BuyLinksEditor bookId={book.id} title={book.title} onClose={() => setEditingLinks(null)} onDirtyChange={setLinksUnsaved} />
              </div>
            )}
          </div>
        );
      })}

      <PaginationBar cur={curPage} pages={pages} total={filtered.length} per={PER}
        onPrev={() => turnPage(p => Math.max(0, p - 1))}
        onNext={() => turnPage(p => Math.min(pages - 1, p + 1))}
      />
    </div>
  );
}

// Ticket 39: the two columns, never combined. Used on every book the admin sees, pending or live.
function ClassificationColumns({ book }) {
  const pop = classifyPopularity(book);
  const label = { fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 };
  return (
    <div data-classification style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
      <div data-col="recommendable">
        <div style={label}>Recommendable</div>
        <Pill color={book.is_author_described ? 'green' : 'gray'}>{book.is_author_described ? 'Yes' : 'No'}</Pill>
      </div>
      <div data-col="popularity">
        <div style={label}>Popularity</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Pill color={pop.cls === 'popular' ? 'blue' : pop.cls === 'gem' ? 'purple' : 'gray'}>{POPULARITY_LABEL[pop.cls]}</Pill>
          {pop.byHand && <Pill color="amber">set by hand</Pill>}
        </div>
        <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', marginTop: 3 }}>{readingsText(book)}</div>
        {pop.byHand && pop.reason && <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', marginTop: 2 }}>Reason: {pop.reason}</div>}
      </div>
    </div>
  );
}

// A read that failed says so. It must not look like "there are none".
function LoadFailed({ what, message }) {
  return (
    <div role="alert" data-load-failed style={{ padding: '12px 14px', background: 'var(--feedback-error-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--radius-md)', color: '#991b1b', fontSize: 'var(--font-size-sm)', lineHeight: 1.5, marginBottom: 'var(--space-md)' }}>
      Couldn&apos;t load {what}: {message}. That is not the same as there being none.
    </div>
  );
}

// Ticket 39: a person's call on one book's popularity. It beats the rule. The
// save goes through admin_set_popularity (admin only); the audit line is written
// by that function itself, in the same call, so a save that lands always has its line.
function PopularityEditor({ book, onClose, onSaved, onUnsure }) {
  const current = classifyPopularity(book);
  const [choice, setChoice] = useState(current.byHand ? current.cls : 'popular');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);

  async function save() {
    if (!reason.trim()) { setProblem('Say why. A reason is required.'); return; }
    setSaving(true); setProblem(null);
    const { error } = await supabase.rpc('admin_set_popularity', { p_book_id: book.id, p_class: choice, p_reason: reason.trim() });
    if (error) {
      // An error with a code came from the database, and the call rolled back. No code means no answer
      // came back, and the save may have gone through.
      if (error.code) { setProblem(`Not saved, nothing was changed. (${error.message})`); setSaving(false); return; }
      setSaving(false);
      onUnsure(`Couldn't tell whether the save worked for "${book.title}", because no answer came back. The list has been read again, so check its row. (${error.message})`);
      return;
    }
    setSaving(false);
    onSaved();
  }

  const options = [
    { id: 'popular', label: 'Popular' },
    { id: 'gem', label: 'Gem' },
    ...(current.byHand ? [{ id: 'automatic', label: 'Back to automatic' }] : []),
  ];

  return (
    <div data-popularity-editor style={{ background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginTop: 'var(--space-sm)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 8 }}>
        Set popularity. {current.byHand ? `Now ${POPULARITY_LABEL[current.cls]} by hand.` : `The rule says ${POPULARITY_LABEL[current.cls]}.`} Your call beats the rule.
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
        {options.map(o => (
          <label key={o.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-700)', cursor: 'pointer' }}>
            <input type="radio" name={`pop-${book.id}`} checked={choice === o.id} onChange={() => setChoice(o.id)} />
            {o.label}
          </label>
        ))}
      </div>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Why? Required."
        style={{ width: '100%', padding: '8px 10px', fontSize: 12.5, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', resize: 'vertical' }} />
      {problem && <div role="alert" style={{ fontSize: 'var(--font-size-xs)', color: '#991b1b', marginTop: 6 }}>{problem}</div>}
      <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 8 }}>
        <Btn small variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        <Btn small onClick={onClose} disabled={saving}>Cancel</Btn>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// RECENTLY PUBLISHED — FIX 1: source filter + FIX 2: published count
// ══════════════════════════════════════════════════════════════════════════════
function RecentlyPublished({ filterSource, onCountChange, emb, onFlagged, onNotice, focusBookId }) {
  const [books, setBooks]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [working, setWorking]         = useState(null);
  const [editingTags, setEditingTags] = useState(null);
  const [editingLinks, setEditingLinks] = useState(null);
  const [linksUnsaved, setLinksUnsaved] = useState(false);   // the open buy-links editor has changes that are not saved
  // Closing, or opening another book's, drops what was not saved, so ask first.
  // Next and Previous: a yes closes the editor (the book's row would leave this page), so a yes does what
  // the question says and the change is gone only because the person said so.
  function turnPage(update) {
    if (!okToLeave()) return;
    setEditingLinks(null);
    setPage(update);
  }
  function toggleLinks(id) {
    if (editingLinks !== null && linksUnsaved && !window.confirm(LINK_WORDS.closeAnyway)) return;
    setEditingLinks(editingLinks === id ? null : id);
  }
  const [search, setSearch]           = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [tagFilter,    setTagFilter]    = useState(null);
  const [page, setPage]                 = useState(0);
  const [popFilter,  setPopFilter]      = useState('all');
  const [editingPop, setEditingPop]     = useState(null);
  const [editingGenres, setEditingGenres] = useState(null);
  const [popNote,    setPopNote]        = useState(null);
  const [loadError,  setLoadError]       = useState(null);
  const [counts,     setCounts]         = useState(null);
  const [countsErr,  setCountsErr]      = useState(null);
  const PER = 10;

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(0); }, [search, sourceFilter, tagFilter, popFilter]);

  // A link to one book (?book=id) opens its buy links, once, when its row is in this list.
  const openedFor = useRef(null);
  useEffect(() => {
    if (focusBookId && openedFor.current !== focusBookId && books.some(b => b.id === focusBookId)) {
      openedFor.current = focusBookId;
      setEditingLinks(focusBookId);
      setPage(0);
    }
  }, [focusBookId, books]);

  // The counts on each side of the line are counted in the database, handed the
  // same two numbers the rule is written with. A failed count says so.
  async function loadCounts() {
    const { data, error } = await supabase.rpc('admin_popularity_counts', { p_editions: POPULAR_EDITIONS, p_ratings: POPULAR_RATINGS });
    if (error) { setCounts(null); setCountsErr(error.message); return; }
    setCounts(Array.isArray(data) ? data[0] : data);
    setCountsErr(null);
  }

  async function load() {
    setLoading(true);
    // Every live book, not the first 500. The pagination total below is this
    // array's length, so a cap made the count wrong AND put the books past it
    // out of reach — there was no page to turn to.
    const buildQuery = () => {
      let q = supabase
        .from('books')
        .select(`id, title, author_name, cover_image_url, status, submission_type, entry_point, created_at, updated_at, live_at, genres, author_id, claimed_at, description_source, reader_description, is_author_described, edition_count, ratings_count, popularity_checked_at, popularity_override:book_popularity_overrides ( class, reason, set_at ), claimant:authors!books_author_id_fkey ( id, display_name, email ), book_tags ( id, tag_type, source, trope:tropes(canonical_name) )`)
        .eq('status', 'live');
      // If used in Author Books section, filter to author submissions only
      if (filterSource) q = q.eq('submission_type', filterSource);
      return q;
    };

    const res = await fetchAllRowsResult(buildQuery);
    if (!res.ok) {
      console.error('[Books] published list failed:', res.error);
      setBooks([]); setLoadError(res.error); setLoading(false);
      return;
    }
    setLoadError(null);
    const data = res.rows;
    const enriched = (data || [])
      // fetchAllRows pages by id; this restores the most-recently-updated-first
      // order the list was previously shown in.
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
      .map(b => ({
        ...b,
        tag_source_counts: {
          author: (b.book_tags || []).filter(t => t.source === 'author').length,
          reader: (b.book_tags || []).filter(t => t.source === 'reader').length,
          api:    (b.book_tags || []).filter(t => t.source === 'api').length,
        },
      }));
    setBooks(enriched);
    onCountChange?.(enriched.length);
    setLoading(false);
    loadCounts();
  }

  async function handleFlag(book) {
    if (!confirm(`Flag "${book.title}"? It leaves the catalog and moves to the Flagged tab. Nothing is deleted, and you can un-flag it.`)) return;
    setWorking(book.id);
    const { error } = await setBookStatus(book, 'flagged', `Flagged "${book.title}" (was live)`);
    if (error) { alert(`"${book.title}" NOT flagged, it is still live: ${error}`); setWorking(null); return; }
    onFlagged?.();
    load(); setWorking(null);
  }

  async function handleUnpublish(book) {
    if (!confirm(`Unpublish "${book.title}"?`)) return;
    setWorking(book.id);
    const { error } = await supabase.from('books').update({ status: 'removed' }).eq('id', book.id);
    if (error) { alert(`"${book.title}" NOT unpublished, it is still live: ${error.message}`); setWorking(null); return; }
    await logAudit('delete', 'book', `Unpublished "${book.title}"`, book.id);
    load(); setWorking(null);
  }

  // PR 3 (admin claim view and un-claim). One admin-gated RPC does the
  // whole revert -- clears author_id/claimed_at, restores the reader's
  // original description, and writes the sticky dismissal -- rather than
  // this doing three separate client calls (see 20260920000000's own
  // comment on why: admins have no direct RLS grant to insert into another
  // author's dismissals row, by design, so this can't be done piecemeal
  // from here the way handleUnpublish/handlePublish are).
  async function handleUnclaim(book) {
    if (!confirm(`Un-claim "${book.title}" from ${book.claimant?.display_name || 'this author'}? It reverts to an unclaimed, reference-only book.`)) return;
    setWorking(book.id);
    const { error } = await supabase.rpc('admin_unclaim_book', { p_book_id: book.id });
    if (error) { alert(`"${book.title}" NOT un-claimed: ${error.message}`); setWorking(null); return; }
    await logAudit('update', 'book', `Un-claimed "${book.title}" from ${book.claimant?.display_name || book.claimant?.email || `author ${book.author_id}`}`, book.id);
    // Ticket 66: the revert put the reader's description back (or cleared it),
    // which changes the text the book is matched on. Re-embed it so it is not
    // left "saved but not searchable". The un-claim HAS happened, so a failure
    // here is its own message, never "NOT un-claimed".
    const re = await requestBookReembed(book.id);
    if (!re.ok) alert(`"${book.title}" was un-claimed, but it could not be re-embedded yet: ${re.reason}`);
    load(); setWorking(null);
  }

  const bySource0 = sourceFilter === 'all' ? books : books.filter(b => b.submission_type === sourceFilter);
  const popOf = (b) => classifyPopularity(b);
  const bySource = popFilter === 'all' ? bySource0
    : popFilter === 'hand' ? bySource0.filter(b => popOf(b).byHand)
    : bySource0.filter(b => popOf(b).cls === popFilter);

  // Compute top 12 tags from current source-filtered set
  const tagCounts = {};
  bySource.forEach(b => {
    (b.book_tags || []).forEach(t => {
      const name = t.trope?.canonical_name;
      if (name) tagCounts[name] = (tagCounts[name] || 0) + 1;
    });
  });
  const topTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([name, count]) => ({ name, count }));

  const byTag = tagFilter
    ? bySource.filter(b => (b.book_tags || []).some(t => t.trope?.canonical_name === tagFilter))
    : bySource;

  const filteredBase = search.trim()
    ? byTag.filter(b =>
        b.title.toLowerCase().includes(search.toLowerCase()) ||
        b.author_name.toLowerCase().includes(search.toLowerCase())
      )
    : byTag;
  const filtered = focusFirst(filteredBase, focusBookId);

  const pages   = Math.max(1, Math.ceil(filtered.length / PER));
  const curPage = Math.min(page, pages - 1);
  const slice   = filtered.slice(curPage * PER, curPage * PER + PER);
  const shown   = keepOpenRow(slice, books, editingLinks, linksUnsaved);

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>;

  return (
    <div>
      {/* Source filter — same as Pending tab */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap', padding: '8px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-10)' }}>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Source:</span>
        {[
          { id: 'all',    label: 'All' },
          { id: 'reader', label: 'Reader' },
          { id: 'author', label: 'Author' },
          { id: 'csv',    label: 'CSV' },
        ].map(f => {
          const count = f.id === 'all' ? books.length : books.filter(b => b.submission_type === f.id).length;
          return (
            <button key={f.id} onClick={() => setSourceFilter(f.id)} style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 14,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
              background: sourceFilter === f.id ? 'var(--neutral-800)' : 'var(--brand-white)',
              color:      sourceFilter === f.id ? 'var(--brand-white)'    : 'var(--neutral-500)',
              border:     sourceFilter === f.id ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
            }}>
              {f.label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {/* Ticket 39: the rule, stated, with a server count on each side of the line */}
      <div data-popularity-rule style={{ padding: '8px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-md)', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--neutral-800)' }}>{POPULAR_LINE_TEXT}.</strong> A book with neither reading is Unknown, not a Gem. A hand-set call beats the rule. Popularity never makes a book recommendable and never raises its rank.
        <div data-popularity-counts style={{ marginTop: 2 }}>
          {counts
            ? <>Across all {Number(counts.live_count).toLocaleString()} live books, from every source: <strong>{Number(counts.popular_count).toLocaleString()}</strong> Popular · <strong>{Number(counts.gem_count).toLocaleString()}</strong> Gem · <strong>{Number(counts.unknown_count).toLocaleString()}</strong> Unknown · {Number(counts.by_hand_count).toLocaleString()} set by hand</>
            : countsErr ? <span style={{ color: '#991b1b' }}>Could not count the books on each side of the line. ({countsErr})</span>
            : 'Counting...'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap', padding: '8px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-md)' }}>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Popularity:</span>
        {[
          { id: 'all',     label: 'All' },
          { id: 'popular', label: 'Popular' },
          { id: 'gem',     label: 'Gem' },
          { id: 'unknown', label: 'Unknown' },
          { id: 'hand',    label: 'Set by hand' },
        ].map(f => {
          const n = f.id === 'all' ? bySource0.length : f.id === 'hand' ? bySource0.filter(b => popOf(b).byHand).length : bySource0.filter(b => popOf(b).cls === f.id).length;
          return (
            <button key={f.id} data-popularity-filter={f.id} onClick={() => setPopFilter(f.id)} style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 14,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
              background: popFilter === f.id ? 'var(--neutral-800)' : 'var(--brand-white)',
              color:      popFilter === f.id ? 'var(--brand-white)'    : 'var(--neutral-500)',
              border:     popFilter === f.id ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
            }}>
              {f.label} ({n})
            </button>
          );
        })}
      </div>

      {/* Top tags — clickable to filter */}
      {topTags.length > 0 && (
        <div style={{ marginBottom: 'var(--space-md)' }}>
          <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
            Top tags {tagFilter && <span onClick={() => setTagFilter(null)} style={{ cursor: 'pointer', color: 'var(--brand-rust)', fontWeight: 700, marginLeft: 'var(--space-6)' }}>✕ clear</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
            {topTags.map(({ name, count }) => {
              const active = tagFilter === name;
              return (
                <button
                  key={name}
                  onClick={() => setTagFilter(active ? null : name)}
                  style={{
                    fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 14,
                    cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: active ? 'var(--neutral-800)' : 'var(--brand-white)',
                    color:      active ? 'var(--brand-white)'    : 'var(--neutral-600)',
                    border:     active ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
                  }}
                >
                  {name}
                  <span style={{ fontSize: 'var(--font-size-xxs)', opacity: 0.7 }}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search published books..."
        style={{ width: '100%', padding: '8px 12px', fontSize: 12.5, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', outline: 'none', fontFamily: 'var(--font-body)', marginBottom: 'var(--space-14)', boxSizing: 'border-box' }}
      />

      {popNote && (
        <div role="alert" data-pop-note style={{ padding: '10px 14px', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', color: 'var(--feedback-warning-ink)', fontSize: 'var(--font-size-sm)', lineHeight: 1.5, marginBottom: 'var(--space-md)' }}>
          {popNote} <button type="button" onClick={() => setPopNote(null)} style={{ marginLeft: 6, background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 'inherit' }}>Dismiss</button>
        </div>
      )}
      {loadError && <LoadFailed what="the published books" message={loadError} />}

      {!loadError && !slice.length && (
        <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>
          {search ? 'No books match your search' : 'No live books yet'}
        </div>
      )}

      {shown.map((book, idx) => {
        const last      = idx === shown.length - 1;
        const busy      = working === book.id;
        const totalTags = Object.values(book.tag_source_counts).reduce((a, b) => a + b, 0);

        return (
          <div key={book.id}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)', padding: '14px 0', borderBottom: last && editingTags !== book.id && editingPop !== book.id && editingGenres !== book.id ? 'none' : '1px solid var(--brand-cream)' }}>
              <BookCover url={book.cover_image_url} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>{book.title}</div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginBottom: 'var(--space-6)' }}>{book.author_name}</div>
                  </div>
                  <Pill color="green">live</Pill>
                </div>
                <ClassificationColumns book={book} />
                <BookPills book={book} embedding={emb} />
                <DescriptionExpander bookId={book.id} />
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
                  {book.tag_source_counts.author > 0 && <TagSourceChip source="author" count={book.tag_source_counts.author} />}
                  {book.tag_source_counts.reader > 0 && <TagSourceChip source="reader" count={book.tag_source_counts.reader} />}
                  {book.tag_source_counts.api    > 0 && <TagSourceChip source="api"    count={book.tag_source_counts.api} />}
                  {totalTags === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No tags</span>}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                  <Btn small variant="amber" onClick={() => setEditingTags(editingTags === book.id ? null : book.id)}>
                    {editingTags === book.id ? 'Close tags' : 'Edit tags'}
                  </Btn>
                  <Btn small onClick={() => toggleLinks(book.id)}>
                    {editingLinks === book.id ? 'Close buy links' : 'Edit buy links'}
                  </Btn>
                  <Btn small onClick={() => setEditingPop(editingPop === book.id ? null : book.id)}>
                    {editingPop === book.id ? 'Close popularity' : 'Set popularity'}
                  </Btn>
                  <Btn small onClick={() => setEditingGenres(editingGenres === book.id ? null : book.id)}>
                    {editingGenres === book.id ? 'Close genres' : 'Genres'}
                  </Btn>
                  <Btn small variant="amber" onClick={() => handleFlag(book)} disabled={busy}>Flag</Btn>
                  <FixAuthorName book={book} onFixed={load} />
                  {book.claimed_at && (
                    <Btn small variant="reject" onClick={() => handleUnclaim(book)} disabled={busy}>Un-claim</Btn>
                  )}
                  <Btn small variant="reject" onClick={() => handleUnpublish(book)} disabled={busy}>Unpublish</Btn>
                </div>
              </div>
            </div>
            {editingGenres === book.id && (
              <div style={{ paddingBottom: 14 }}>
                <GenresPanel book={book} onClose={() => setEditingGenres(null)} onChanged={(text) => { onNotice?.(text); load(); onFlagged?.(); }} />
              </div>
            )}
            {editingPop === book.id && (
              <div style={{ paddingBottom: 14 }}>
                <PopularityEditor book={book}
                  onClose={() => setEditingPop(null)}
                  onSaved={() => { setEditingPop(null); setPopNote(null); load(); }}
                  onUnsure={(text) => { setPopNote(text); setEditingPop(null); load(); }} />
              </div>
            )}
            {editingTags === book.id && (
              <div style={{ paddingBottom: last ? 0 : 14, borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
                <TagEditor bookId={book.id} onClose={() => setEditingTags(null)} />
              </div>
            )}
            {editingLinks === book.id && (
              <div style={{ paddingBottom: last ? 0 : 14, borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
                <BuyLinksEditor bookId={book.id} title={book.title} onClose={() => setEditingLinks(null)} onDirtyChange={setLinksUnsaved} />
              </div>
            )}
          </div>
        );
      })}

      <PaginationBar cur={curPage} pages={pages} total={filtered.length} per={PER}
        onPrev={() => turnPage(p => Math.max(0, p - 1))}
        onNext={() => turnPage(p => Math.min(pages - 1, p + 1))}
      />
    </div>
  );
}

// Flagged books, and books that look like test data. Candidates are guesses, never facts.
function StatusList({ kind, emb, onCountChange, onChanged }) {
  const [books, setBooks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [working, setWorking] = useState(null);
  const [page, setPage]       = useState(0);
  const PER = 10;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchAllRowsResult(() => {
      const q = supabase.from('books').select(BOOK_ROW_FIELDS);
      return kind === 'flagged' ? q.eq('status', 'flagged') : q.in('status', ['live', 'pending']);
    });
    if (!res.ok) {
      console.error(`[Books] ${kind} list failed:`, res.error);
      setBooks([]); setLoadError(res.error); setLoading(false);
      onCountChange?.(null);
      return;
    }
    setLoadError(null);
    const rows = kind === 'flagged'
      ? res.rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      : sortTestData(res.rows.filter(b => testDataReasons(b).length > 0));
    setBooks(rows);
    onCountChange?.(rows.length);
    setLoading(false);
  }, [kind, onCountChange]);

  useEffect(() => { load(); }, [load]);

  async function handleFlag(book) {
    if (!confirm(`Flag "${book.title}" as test data? It leaves the catalog and moves to the Flagged tab. Nothing is deleted, and you can un-flag it.`)) return;
    setWorking(book.id);
    const { error } = await setBookStatus(book, 'flagged', `Flagged "${book.title}" as test data (was ${book.status})`);
    if (error) { alert(`"${book.title}" NOT flagged, it is still ${book.status}: ${error}`); setWorking(null); return; }
    onChanged?.();
    await load(); setWorking(null);
  }

  async function handleUnflag(book) {
    if (!confirm(`Un-flag "${book.title}"? It goes back to Pending.`)) return;
    setWorking(book.id);
    const { error } = await setBookStatus(book, 'pending', `Un-flagged "${book.title}" (back to pending)`);
    if (error) { alert(`"${book.title}" NOT un-flagged, it is still flagged: ${error}`); setWorking(null); return; }
    onChanged?.();
    await load(); setWorking(null);
  }

  const pages   = Math.max(1, Math.ceil(books.length / PER));
  const curPage = Math.min(page, pages - 1);
  const slice   = books.slice(curPage * PER, curPage * PER + PER);

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>;

  return (
    <div>
      {kind === 'test' && (
        <p data-test-note style={{ margin: '0 0 var(--space-md)', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
          Candidates, not facts: a guess from the title and the author's name, over live and pending books. A swipeable one (live and author-described) can reach the swipe deck, so those come first.
        </p>
      )}
      {loadError && <LoadFailed what={kind === 'flagged' ? 'the flagged books' : 'the test-data candidates'} message={loadError} />}
      {!loadError && !slice.length && (
        <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>
          {kind === 'flagged' ? 'No flagged books' : 'No books look like test data'}
        </div>
      )}
      {slice.map((book, idx) => {
        const last = idx === slice.length - 1;
        const busy = working === book.id;
        const reasons = testDataReasons(book);
        return (
          <div key={book.id} data-status-row style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)', padding: '14px 0', borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
            <BookCover url={book.cover_image_url} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>{book.title}</div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginBottom: 'var(--space-6)' }}>{book.author_name}</div>
                </div>
                <Pill color={book.status === 'live' ? 'green' : book.status === 'flagged' ? 'red' : 'amber'}>{book.status}</Pill>
              </div>
              {kind === 'test' && (
                <div style={{ marginBottom: 'var(--space-sm)', fontSize: 'var(--font-size-xs)', lineHeight: 1.5 }}>
                  <span style={{ color: 'var(--neutral-600)' }}>Looks like test data: {reasons.join('; ')}.</span>
                  {isSwipeable(book) && <strong data-swipeable style={{ marginLeft: 6, color: '#991b1b' }}>Swipeable: live and author-described, so it can reach the deck.</strong>}
                </div>
              )}
              <ClassificationColumns book={book} />
              <BookPills book={book} embedding={emb} />
              <DescriptionExpander bookId={book.id} />
              <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                {kind === 'test'
                  ? <Btn small variant="amber" onClick={() => handleFlag(book)} disabled={busy}>Flag as test data</Btn>
                  : <Btn small onClick={() => handleUnflag(book)} disabled={busy}>Un-flag (back to pending)</Btn>}
                <FixAuthorName book={book} onFixed={load} />
              </div>
            </div>
          </div>
        );
      })}
      <PaginationBar cur={curPage} pages={pages} total={books.length} per={PER}
        onPrev={() => setPage(p => Math.max(0, p - 1))}
        onNext={() => setPage(p => Math.min(pages - 1, p + 1))}
      />
    </div>
  );
}

// Which author-described books have no matching data yet, or a stale one. The same two sets Health uses.
function useEmbeddingStates() {
  const [emb, setEmb] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      const [a, b] = await Promise.all([
        fetchAllRowsResult(() => SETS.nodata(supabase.from('books').select('id'))),
        fetchAllRowsResult(() => SETS.stale(supabase.from('books').select('id'))),
      ]);
      if (!live) return;
      if (!a.ok || !b.ok) { console.error('[Books] embedding states failed:', a.error || b.error); setEmb({ failed: true }); return; }
      setEmb({ nodata: new Set(a.rows.map(r => r.id)), stale: new Set(b.rows.map(r => r.id)) });
    })();
    return () => { live = false; };
  }, []);
  return emb;
}

// ══════════════════════════════════════════════════════════════════════════════
// STATS BAR — every count is a server count; one that could not be read shows a dash, not 0
// ══════════════════════════════════════════════════════════════════════════════
function StatsBar({ isMobile, pendingCount }) {
  const [stats, setStats] = useState({ total: null, live: null, removed: null });
  const [noAuthor, setNoAuthor] = useState({ all: null, readerCopy: null });

  useEffect(() => {
    async function load() {
      const [totalR, liveR, removedR] = await Promise.all([
        supabase.from('books').select('*', { count: 'exact', head: true }),
        supabase.from('books').select('*', { count: 'exact', head: true }).eq('status', 'live'),
        supabase.from('books').select('*', { count: 'exact', head: true }).eq('status', 'removed'),
      ]);
      const pick = (r, what) => {
        if (r.error || typeof r.count !== 'number') {
          console.error(`[Books] ${what} count failed:`, r.error?.message);
          return null;
        }
        return r.count;
      };
      setStats({ total: pick(totalR, 'all books'), live: pick(liveR, 'live'), removed: pick(removedR, 'removed') });
      const { counts } = await loadCounts(['noauthor', 'readerCopy'], 'Books');
      setNoAuthor({ all: counts.noauthor, readerCopy: counts.readerCopy });
    }
    load();
  }, []);

  const cards = [
    { label: 'All books',       value: stats.total,   accent: '#2563eb', tip: COUNT_TIPS.booksTotal },
    { label: 'Pending review',  value: pendingCount,  accent: '#d97706', tip: COUNT_TIPS.booksPending },
    { label: 'Live in catalog', value: stats.live,    accent: '#16a34a', tip: COUNT_TIPS.booksLive },
    { label: 'Removed',         value: stats.removed, accent: '#dc2626', tip: COUNT_TIPS.booksRemoved },
    { label: 'No author description', value: noAuthor.all, accent: '#9333ea',
      sub: typeof noAuthor.readerCopy === 'number' ? `${noAuthor.readerCopy.toLocaleString()} of them ${noAuthor.readerCopy === 1 ? 'has' : 'have'} reader copy` : undefined,
      tip: 'Live books with no description an author wrote. They are in the catalog and turn up in search, but the matchmaker cannot return them. The line underneath says how many of them carry reader copy, a description a reader wrote or one archived beside an author\'s, so this is not a count of blank pages. Counted in the database. A dash means the count could not be loaded.' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: isMobile ? 10 : 12, marginBottom: 22 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderTop: `3px solid ${c.accent}`, borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{c.label}</span>
            <InfoTip text={c.tip} />
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>{typeof c.value === 'number' ? c.value.toLocaleString() : '—'}</div>
          {c.sub && <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', marginTop: 6 }}>{c.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT — FIX 4: publishedCount + filterSource prop
// ══════════════════════════════════════════════════════════════════════════════
export default function BooksSection({ onBadgeChange, defaultTab = 'pending', filterSource, isMobile }) {
  const [tab,            setTab]            = useState(defaultTab);
  const [pendingCount,   setPendingCount]   = useState(null);
  const [publishedCount, setPublishedCount] = useState(0);
  const [flaggedCount,   setFlaggedCount]   = useState(null);
  const [testCount,      setTestCount]      = useState(null);
  const emb = useEmbeddingStates();
  const [notice, setNotice] = useState(null);

  // A link to one book: /admin/r-books?book=12. It opens the tab the book is on, with its row first and
  // its buy links open. Only Pending and Published have the buy-links button, so a book on neither says
  // what its status is. No book with that id says so.
  const [bookParam] = useTabParam('book', '');
  const [focusBookId, setFocusBookId] = useState(null);
  useEffect(() => {
    setFocusBookId(null);
    if (!bookParam) return undefined;
    if (!/^\d+$/.test(bookParam)) { setNotice(LINK_WORDS.noSuchBook); return undefined; }
    const id = Number(bookParam);
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('books').select('id, status').eq('id', id).maybeSingle();
      if (cancelled) return;
      if (error) { setNotice(`Could not look that book up: ${error.message}`); return; }
      if (!data) { setNotice(LINK_WORDS.noSuchBook); return; }
      if (data.status === 'pending') { setTab('pending'); setFocusBookId(id); }
      else if (data.status === 'live') { setTab('published'); setFocusBookId(id); }
      else setNotice(LINK_WORDS.notOnEitherTab(data.status));
    })();
    return () => { cancelled = true; };
  }, [bookParam]);

  // Server counts for the tabs. Pending is counted here too, so the Pending review card has a number
  // when the screen opens on another tab. A failed read leaves null: a dash on the card, no count on the tab.
  const refreshFlagged = useCallback(async () => {
    const [f, p] = await Promise.all([
      supabase.from('books').select('id', { count: 'exact', head: true }).eq('status', 'flagged'),
      supabase.from('books').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    ]);
    if (f.error) { console.error('[Books] flagged count failed:', f.error.message); setFlaggedCount(null); }
    else setFlaggedCount(f.count);
    if (p.error) { console.error('[Books] pending count failed:', p.error.message); setPendingCount(null); }
    else setPendingCount(p.count);
  }, []);
  useEffect(() => { refreshFlagged(); }, [refreshFlagged]);

  const TABS = [
    { id: 'published', label: `Published${publishedCount > 0 ? ` (${publishedCount})` : ''}` },
    { id: 'pending',   label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
    { id: 'flagged',   label: `Flagged${flaggedCount > 0 ? ` (${flaggedCount})` : ''}` },
    { id: 'test',      label: `Looks like test data${testCount > 0 ? ` (${testCount})` : ''}` },
  ];

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 'var(--space-none)' }}>Books</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)' }}>Review and publish submissions · edit tags before going live</p>
        <p data-live-note style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', lineHeight: 1.5 }}>
          &ldquo;Live&rdquo; on this page means the book&apos;s status is live: it is in the catalog and turns up in search. It is not what an author means by live, which also needs a description and its matching numbers.
        </p>
      </div>

      <StatsBar isMobile={isMobile} pendingCount={pendingCount} />

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px' : '20px 22px' }}>
        {!isMobile && (
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-cream)', borderRadius: 'var(--radius-md)', marginBottom: 18, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
            <span style={{ fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 'var(--font-size-xxs)' }}>Tag sources:</span>
            {Object.entries(SOURCE_STYLES).map(([key, s]) => (
              <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: 'var(--radius-circle)', background: s.dot }} />
                <strong style={{ color: 'var(--neutral-800)', textTransform: 'capitalize' }}>{key}:</strong>
                <span style={{ color: 'var(--neutral-400)' }}>{s.tip}</span>
              </span>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', borderBottom: '2px solid var(--brand-sand)', marginBottom: 18, marginTop: -4, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => { if (okToLeave()) setTab(t.id); }} style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: isMobile ? '8px 12px' : '8px 16px',
              color: tab === t.id ? 'var(--neutral-800)' : 'var(--neutral-400)', border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {emb?.failed && (
          <div role="alert" data-embedding-failed style={{ margin: '0 0 var(--space-md)', padding: '8px 12px', background: 'var(--feedback-error-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', color: '#991b1b' }}>
            Couldn&apos;t read which books have matching data, so those pills are missing.
          </div>
        )}
        {notice && (
          <div role="status" data-status-notice style={{ margin: '0 0 var(--space-md)', padding: '8px 12px', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 'var(--font-size-md)', lineHeight: 1 }}>✕</button>
          </div>
        )}
        {tab === 'pending'   && <PendingQueue onCountChange={setPendingCount} onBadgeChange={onBadgeChange} isMobile={isMobile} emb={emb} onFlagged={refreshFlagged} onNotice={setNotice} focusBookId={focusBookId} />}
        {tab === 'published' && <RecentlyPublished filterSource={filterSource} onCountChange={setPublishedCount} emb={emb} onFlagged={refreshFlagged} onNotice={setNotice} focusBookId={focusBookId} />}
        {tab === 'flagged'   && <StatusList kind="flagged" emb={emb} onCountChange={setFlaggedCount} onChanged={refreshFlagged} />}
        {tab === 'test'      && <StatusList kind="test" emb={emb} onCountChange={setTestCount} onChanged={refreshFlagged} />}
      </div>
    </div>
  );
}